import type { ReactElement } from 'react'
import { Routes, Route, Navigate, useParams, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  supportedLanguages,
  defaultLanguage,
  type SupportedLanguage,
} from '@/i18n'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'
import { Header, Footer } from '@/components/layout'
import { FinalCTA } from '@/components/FinalCTA'
import HomePage from '@/pages/HomePage'
import AboutPage from '@/pages/AboutPage'
import ServicesPage from '@/pages/ServicesPage'
import MachinesPage from '@/pages/MachinesPage'
import MachineDetailPage from '@/pages/MachineDetailPage'
import NewsPage from '@/pages/NewsPage'
import NewsDetailPage from '@/pages/NewsDetailPage'
import EventsPage from '@/pages/EventsPage'
import EventDetailPage from '@/pages/EventDetailPage'
import PartnersPage from '@/pages/PartnersPage'
import ContactPage from '@/pages/ContactPage'
import PrivacyPage from '@/pages/PrivacyPage'
import CookiePolicyPage from '@/pages/CookiePolicyPage'
import LegalNoticePage from '@/pages/LegalNoticePage'

function LanguageWrapper() {
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation()

  // Redirect invalid language to default
  if (!lang || !supportedLanguages.includes(lang as SupportedLanguage)) {
    return <Navigate to={`/${defaultLanguage}`} replace />
  }

  if (i18n.language !== lang) {
    i18n.changeLanguage(lang)
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <div className="flex-1">
        <Outlet />
      </div>
      <FinalCTA />
      <Footer />
    </div>
  )
}

/**
 * Maps each route key to its page element plus an optional detail page
 * mounted at `:slug`. Keeping this table here keeps the router in sync with
 * `localizedRoutes`: add a new key there and here, paths for every supported
 * language are registered automatically.
 */
const routeElements: Record<
  RouteKey,
  { element: ReactElement; detail?: ReactElement }
> = {
  home: { element: <HomePage /> },
  about: { element: <AboutPage /> },
  services: { element: <ServicesPage /> },
  machines: { element: <MachinesPage />, detail: <MachineDetailPage /> },
  news: { element: <NewsPage />, detail: <NewsDetailPage /> },
  events: { element: <EventsPage />, detail: <EventDetailPage /> },
  partners: { element: <PartnersPage /> },
  contacts: { element: <ContactPage /> },
  privacy: { element: <PrivacyPage /> },
  cookiePolicy: { element: <CookiePolicyPage /> },
  legalNotice: { element: <LegalNoticePage /> },
}

function buildLocalizedRoutes(): ReactElement[] {
  const seen = new Set<string>()
  const routes: ReactElement[] = []

  for (const [key, cfg] of Object.entries(routeElements) as [
    RouteKey,
    (typeof routeElements)[RouteKey],
  ][]) {
    const paths = new Set(Object.values(localizedRoutes[key]))
    for (const path of paths) {
      if (!path) continue // home is handled by <Route index />
      if (seen.has(path)) continue
      seen.add(path)
      routes.push(<Route key={path} path={path} element={cfg.element} />)
      if (cfg.detail) {
        const slugPath = `${path}/:slug`
        seen.add(slugPath)
        routes.push(
          <Route key={slugPath} path={slugPath} element={cfg.detail} />
        )
      }
    }
  }

  return routes
}

export function LanguageRouter() {
  return (
    <Routes>
      {/* Redirect root to default language */}
      <Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />

      {/* Language-prefixed routes */}
      <Route path="/:lang" element={<LanguageWrapper />}>
        <Route index element={<HomePage />} />
        {buildLocalizedRoutes()}
      </Route>
    </Routes>
  )
}
